import { useEffect, useMemo, useState } from "react";
import services from "../../../services/services";
import _ from "underscore";
import { decryptData, encryptData } from "../../../utils/encrypt";
import { convertTo12HourFormat, dateConvert, formatDateObject, timeStringToDate } from "../../../utils/Helper";
import { DateObject } from "react-multi-date-picker";

export const useDatabaseDeadlock = ({ projectChanged }) => {

  const [deadlockData, setDeadlockData] = useState({ x_axis: [], y_axis: [], api_data: [] });
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState([]);
  const [showMore, setShowMore] = useState({ sql: { visible: false, index: null }, raw_data: { visible: false, index: null } });
  const [hoveredRange, setHoveredRange] = useState([]);
  const [loader, setLoader] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [filterOpts, setFilterOpts] = useState({ dbname: [] });
  const [filterValues, setFilterValues] = useState({ dbname: [], date: {} });
  const [filterCount, setFilterCount] = useState(0);
  const [expand, setExpand] = useState(false);
  const [time, setTime] = useState({ from: null, to: null });
  const [intervalFlag, setIntervalFlag] = useState(false);
  const [defaultClicked, setDefaultClicked] = useState(true);

  useEffect(() => {
    handleDateRange(null, true);
  }, [])

  useEffect(() => {
    getData()
  }, [projectChanged])

  useEffect(() => {
    const interval = setInterval(() => {
      if (defaultClicked) {
        handleDateRange(null, true);
      } else {
        getData();
      }
    }, 120000);

    return () => clearInterval(interval);
  }, [intervalFlag]);

  const handleDateRange = (e, defaultFlag) => {
    if (e === null) {
      const now = new DateObject();
      const oneMinuteAgo = new DateObject().subtract(1, "hour");
      const range = [new DateObject(oneMinuteAgo), new DateObject(now)];
      setHoveredRange(range);
      setTime({ from: oneMinuteAgo?.toDate(), to: now?.toDate() });
      defaultFlag && handleApplyFilters(range) && setDefaultClicked(true);
    } else {
      setHoveredRange(e);
      setDefaultClicked(false);
    }
  }

  const handleApplyFilters = (dateRange) => {
    let dates;
    if (dateRange) {
      dates = {
        from_date: dateConvert(dateRange?.[0]?.toDate(), "iso-string")?.split(".")?.[0],
        to_date: dateConvert(dateRange?.[1]?.toDate(), "iso-string")?.split(".")?.[0]
      }
    } else {
      dates = {
        from_date: formatDateObject(hoveredRange?.[0], time?.from),
        to_date: formatDateObject(hoveredRange?.[1], time?.to)
      }
    }
    const deadlockFilters = {
      dates: dates,
      dbname: filterValues?.dbname
    }
    sessionStorage.setItem("deadlockfilters", encryptData(JSON.stringify(deadlockFilters)));

    // set count of filters applied
    let count = 0;
    if ((hoveredRange === undefined || _.isEmpty(hoveredRange)) && (dateRange === undefined || _.isEmpty(dateRange))) {
      count = 0;
    } else {
      count++;
    }
    Object.keys(filterValues)?.forEach((item) => {
      if (filterValues[item].length > 0) {
        count++;
      }
    })
    setFilterCount(count);
    setShowFilter(false);
    sessionStorage.getItem("inspectra_error_view") && getData();
    setIntervalFlag(!intervalFlag);
  }

  const handleDefaultFilters = () => {
    handleDateRange(null, false);
    setDefaultClicked(true);
    setFilterValues((prev) => ({ ...prev, dbname: [] }));
  }

  const getData = async () => {
    try {
      const selectedProject = sessionStorage.getItem("inspectra_project") ? JSON.parse(decryptData(sessionStorage.getItem("inspectra_project"))) : {};
      if (!_.isEmpty(selectedProject) && selectedProject?.host) {
        setLoader(true);
        const filters = JSON.parse(decryptData(sessionStorage.getItem("deadlockfilters")) || "{}");
        setFilterValues((prev) => ({ ...prev, date: filters?.dates }));
        const payload = {
          from_datetime: filters?.dates?.from_date,
          to_datetime: filters?.dates?.to_date,
          dbname: _.isEmpty(filters?.dbname) ? undefined : filters?.dbname?.toString(),
          project_name: selectedProject?.project_key
        }
        const hostArray = _.pluck(selectedProject?.host, "domain_host_key");
        const responses = await Promise.all(
          hostArray.map(async (host) => {
            try {
              const response = await services.callInspectra("POST", '/inspectra/deadlock/v1', payload, host);
              return response?.data?.[0];
            } catch (err) {
              console.error(`Error fetching data for host ${host}`, err);
              return null;
            }
          })
        );

        const dataArray = responses.filter(Boolean).flat();
        if (dataArray) {
          const data = dataArray;
          const dbnames = _.uniq(_.pluck(data, "dbname"));
          if (_.isEmpty(filterValues?.dbname)) {
            setFilterOpts({ dbname: dbnames });
          }
          setDeadlockData((prev) => ({ ...prev, api_data: data }));

          const intervalBoundaries = getEqualTimeIntervals(filters?.dates?.from_date, filters?.dates?.to_date, 6)?.intervals;
          const fullDateIntervals = getEqualTimeIntervals(filters?.dates?.from_date, filters?.dates?.to_date, 6)?.fullDateIntervals;
          const intervalLabels = intervalBoundaries?.map((item) => {
            let date = item?.split("T");
            if (date?.[1]) {
              date[1] = convertTo12HourFormat(date?.[1]);
            }
            const itemm = date.join(", ");
            return itemm;
          })
          const dataCounts = groupDeadlocksByInterval(data, fullDateIntervals, intervalLabels)?.counts;
          const dataObj = groupDeadlocksByInterval(data, fullDateIntervals, intervalLabels)?.dataObj;

          setDeadlockData((prev) => ({ ...prev, x_axis: intervalLabels, y_axis: dataCounts, data_obj: dataObj }))

          setLoader(false);
        }
      }
    } catch (error) {
      console.error("error: ", error);
      setLoader(false)
    }
  }

  const groupDeadlocksByInterval = (data, fullDateIntervals, intervalLabels) => {
    const counts = new Array(fullDateIntervals.length).fill(0);
    const dataObj = {};
    intervalLabels?.forEach((item) => {
      dataObj[item] = []
    })
    data?.forEach(item => {
      const deadlockTime = dateConvert(new Date(item.DeadlockTime), "iso-string");
      const deadlockDate = new Date(deadlockTime);

      for (let i = 0; i < fullDateIntervals.length - 1; i++) {
        const start = fullDateIntervals[i];
        const end = fullDateIntervals[i + 1];
        const startDate = start?.split("T")?.length === 1 ? timeStringToDate(start) : new Date(start);
        const endDate = start?.split("T")?.length === 1 ? timeStringToDate(end) : new Date(end);

        if (deadlockDate >= startDate && deadlockDate < endDate) {
          counts[i + 1]++;
          dataObj[intervalLabels?.[i + 1]].push(item);
          break;
        }

      }
    });

    return { counts, dataObj };
  }

  const getEqualTimeIntervals = (from, to, numberOfIntervals) => {
    const start = new Date(from);
    const end = new Date(to);

    const isSameDay =
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate();

    const intervalMillis = (end - start) / numberOfIntervals;
    const intervals = [];
    const fullDateIntervals = [];

    for (let i = 0; i <= numberOfIntervals; i++) {
      const current = new Date(start.getTime() + i * intervalMillis);
      intervals.push(isSameDay ? convertTo12HourFormat(current.toTimeString().slice(0, 8)) : dateConvert(current, "iso-string"));
      fullDateIntervals.push(dateConvert(current, "iso-string"));
    }

    return { intervals, fullDateIntervals };
  }

  const options = {
    credits: { enabled: false },
    chart: {
      type: 'line',
      events: {
        render: function () {
          const chart = this;
          chart.xAxis[0].ticks &&
            Object.values(chart.xAxis[0].ticks).forEach(tick => {
              if (tick.label && !tick.label.clickAttached) {
                tick.label.element.style.cursor = 'pointer';
                tick.label.element.onclick = () => {
                  handleShowDetailsPopup(tick.label.textStr);
                };
                tick.label.clickAttached = true;
              }
            });
        }
      }
    },
    title: {
      text: ''
    },
    xAxis: {
      categories: deadlockData?.x_axis,
      gridLineDashStyle: 'solid',
      gridLineWidth: 1
    },
    yAxis: {
      tickInterval: 15,
      title: {
        text: 'Deadlocks Count'
      },
    },
    tooltip: {
      enabled: true,
      split: false,
    },
    plotOptions: {
      series: {
        cursor: 'pointer',
        point: {
          events: {
            click: function () {
              handleShowDetailsPopup(this.category);
            }
          }
        },
        enableMouseTracking: true,
        showInLegend: false,
      },
    },
    series: [
      {
        name: "Deadlocks",
        data: deadlockData?.y_axis
      },
    ],
  }

  const handleShowDetailsPopup = (date) => {
    const data = deadlockData?.data_obj?.[date];
    if (!_.isEmpty(data)) {
      setPopupData(data);
      setShowPopup(true);
    }
  }

  return useMemo(() => ({
    options, showPopup, setShowPopup, popupData, showMore, setShowMore, hoveredRange, setHoveredRange, loader, showFilter, setShowFilter, filterOpts, filterValues, setFilterValues, filterCount, expand, setExpand, time, setTime, handleDateRange, handleApplyFilters, handleDefaultFilters
  }), [options, showPopup, setShowPopup, popupData, showMore, setShowMore, hoveredRange, setHoveredRange, loader, showFilter, setShowFilter, filterOpts, filterValues, setFilterValues, filterCount, expand, setExpand, time, setTime, handleDateRange, handleApplyFilters, handleDefaultFilters])
}